import { useCallback, useEffect, useState } from 'react';
import { ActiveOffer, ApiError, api, KpiData } from '../api/client';
import { useLayoutCounts } from '../components/Layout';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../hooks/useToast';
import ToastStack from '../components/Toast';

const ACTIVE_OFFERS_PAGE_SIZE = 10;
// Offers here are short-lived by design (the whole point of the LIVE feed),
// so the realistic ceiling on how many can be simultaneously active is in the
// tens, not thousands — 500 leaves generous headroom while still bounding a
// runaway fetch loop if that assumption is ever wrong.
const KILL_SEARCH_MAX_PAGES = 10;
const KILL_SEARCH_PAGE_SIZE = 50;
const KILL_SEARCH_MAX_RESULTS = 8;

interface KillTarget {
  id: string;
  title?: string;
}

function fmtSeconds(s: number): string {
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m${Math.round(s % 60)}s`;
}

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-CA', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function formatTimeRemaining(seconds: number): string {
  if (seconds <= 0) return 'Expiration imminente';

  const totalMinutes = Math.floor(seconds / 60);
  if (totalMinutes < 1) return `Expire dans ${seconds}s`;
  if (totalMinutes < 60) return `Expire dans ${totalMinutes} min`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) {
    return `Expire dans ${hours} h${minutes > 0 ? ` ${minutes} min` : ''}`;
  }

  const days = Math.floor(hours / 24);
  return `Expire dans ${days} j ${hours % 24} h`;
}

export default function Dashboard() {
  const { counts, navigate } = useLayoutCounts();
  const [kpi, setKpi] = useState<KpiData | null>(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [activeOffers, setActiveOffers] = useState<ActiveOffer[]>([]);
  const [activeLoading, setActiveLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [activeError, setActiveError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [activeServerTime, setActiveServerTime] = useState<string | null>(null);
  const [killTarget, setKillTarget] = useState<KillTarget | null>(null);
  const [killingId, setKillingId] = useState<string | null>(null);
  const [killSearchQuery, setKillSearchQuery] = useState('');
  // null = not loaded yet for this dashboard visit. Loaded once, lazily, on
  // first use of the search box — a manual page reload refreshes it, which is
  // an acceptable staleness window for what is meant to be an occasional
  // fallback, not the primary way of finding an offer.
  const [killSearchPool, setKillSearchPool] = useState<ActiveOffer[] | null>(
    null,
  );
  const [killSearchLoading, setKillSearchLoading] = useState(false);
  const { toasts, push, dismiss } = useToast();

  const loadKpi = useCallback(async () => {
    setKpiLoading(true);
    try {
      const response = await api.getKpi();
      setKpi(response.data);
    } catch {
      setKpi(null);
    } finally {
      setKpiLoading(false);
    }
  }, []);

  const loadActiveOffers = useCallback(
    async (cursor?: string, append = false) => {
      if (append) {
        setMoreLoading(true);
      } else {
        setActiveLoading(true);
        setActiveError(null);
      }

      try {
        const response = await api.getActiveOffers(
          cursor,
          ACTIVE_OFFERS_PAGE_SIZE,
        );
        setActiveOffers((previous) => {
          if (!append) return response.data;

          const knownIds = new Set(previous.map((offer) => offer.id));
          return [
            ...previous,
            ...response.data.filter((offer) => !knownIds.has(offer.id)),
          ];
        });
        setActiveServerTime(response.server_time);
        setNextCursor(response.next_cursor);
        setHasMore(response.has_more);
        setActiveError(null);
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : 'Impossible de charger les offres actives.';
        if (append) {
          push(message, 'error');
        } else {
          setActiveError(message);
        }
      } finally {
        if (append) {
          setMoreLoading(false);
        } else {
          setActiveLoading(false);
        }
      }
    },
    [push],
  );

  const refreshLiveData = useCallback(async () => {
    await Promise.allSettled([loadKpi(), loadActiveOffers()]);
  }, [loadActiveOffers, loadKpi]);

  // Walks every page of active offers once, independent of the main list's
  // own pagination — the point of this search is to find an offer that is
  // NOT on the loaded page, so it can't reuse that page's cursor state.
  const loadKillSearchPool = useCallback(async () => {
    if (killSearchPool !== null || killSearchLoading) return;
    setKillSearchLoading(true);
    try {
      const collected: ActiveOffer[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < KILL_SEARCH_MAX_PAGES; page += 1) {
        const response = await api.getActiveOffers(
          cursor,
          KILL_SEARCH_PAGE_SIZE,
        );
        collected.push(...response.data);
        if (!response.has_more || !response.next_cursor) break;
        cursor = response.next_cursor;
      }
      setKillSearchPool(collected);
    } catch {
      // Silent: the search box degrades to "no results" rather than
      // interrupting the admin with an error for what is an optional aid.
      setKillSearchPool([]);
    } finally {
      setKillSearchLoading(false);
    }
  }, [killSearchPool, killSearchLoading]);

  const killSearchResults =
    killSearchPool && killSearchQuery.trim().length > 0
      ? killSearchPool
          .filter((offer) =>
            offer.title
              .toLowerCase()
              .includes(killSearchQuery.trim().toLowerCase()),
          )
          .slice(0, KILL_SEARCH_MAX_RESULTS)
      : [];

  useEffect(() => {
    void loadKpi();
    void loadActiveOffers();
  }, [loadActiveOffers, loadKpi]);

  async function handleKill() {
    if (!killTarget) return;

    setKillingId(killTarget.id);
    try {
      await api.killOffer(killTarget.id);
      push(`« ${killTarget.title ?? killTarget.id} » retirée avec succès.`, 'success');
      setKillSearchQuery('');
      setKillSearchPool((pool) =>
        pool ? pool.filter((offer) => offer.id !== killTarget.id) : pool,
      );
      setKillTarget(null);
      await refreshLiveData();
    } catch (error: unknown) {
      push(
        error instanceof Error ? error.message : 'Erreur lors du retrait.',
        'error',
      );

      if (
        error instanceof ApiError &&
        (error.status === 404 || error.status === 409)
      ) {
        setKillSearchPool((pool) =>
          pool ? pool.filter((offer) => offer.id !== killTarget.id) : pool,
        );
        setKillTarget(null);
        await refreshLiveData();
      }
    } finally {
      setKillingId(null);
    }
  }

  async function copyOfferId(id: string) {
    try {
      await navigator.clipboard.writeText(id);
      push('UUID copié.', 'success');
    } catch {
      push("Impossible de copier l'UUID.", 'error');
    }
  }

  const needsAttention = counts.offers + counts.hosts;

  return (
    <>
      <ToastStack toasts={toasts} dismiss={dismiss} />

      {needsAttention > 0 && (
        <div className="attention-banner">
          <div className="attention-copy">
            <span className="attention-icon">⚠</span>
            <div>
              <div className="attention-title">
                {needsAttention} élément{needsAttention > 1 ? 's' : ''} nécessite
                {needsAttention > 1 ? 'nt' : ''} votre attention
              </div>
              <div className="attention-subtitle">
                {counts.offers} offre{counts.offers > 1 ? 's' : ''} en attente ·{' '}
                {counts.hosts} hôte{counts.hosts > 1 ? 's' : ''} à valider
              </div>
            </div>
          </div>
          <div className="attention-actions">
            {counts.offers > 0 && (
              <a
                href="/offers"
                className="btn btn-primary btn-sm"
                onClick={(event) => {
                  event.preventDefault();
                  navigate('/offers');
                }}
              >
                Voir les offres
              </a>
            )}
            {counts.hosts > 0 && (
              <a
                href="/hosts"
                className="btn btn-ghost btn-sm"
                onClick={(event) => {
                  event.preventDefault();
                  navigate('/hosts');
                }}
              >
                Voir les hôtes
              </a>
            )}
          </div>
        </div>
      )}

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Offres en attente</div>
          <div className={`kpi-value${counts.offers > 0 ? ' accent' : ''}`}>
            {counts.offers}
          </div>
          <div className="kpi-sub">à modérer</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Hôtes à valider</div>
          <div className={`kpi-value${counts.hosts > 0 ? ' accent' : ''}`}>
            {counts.hosts}
          </div>
          <div className="kpi-sub">demandes pendantes</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Offres actives (live)</div>
          {kpiLoading ? (
            <div className="kpi-value muted">—</div>
          ) : kpi ? (
            <>
              <div className="kpi-value success">{kpi.live_offers_count}</div>
              <div className="kpi-sub">sur le feed</div>
            </>
          ) : (
            <div className="kpi-value muted">N/D</div>
          )}
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Taux de conversion</div>
          {kpi ? (
            <>
              <div className="kpi-value success">
                {kpi.saved_percentage.toFixed(1)}%
              </div>
              <div className="kpi-sub">réservées avant expiration</div>
            </>
          ) : (
            <div className="kpi-value muted">—</div>
          )}
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Temps moyen booking</div>
          {kpi ? (
            <>
              <div className="kpi-value">
                {kpi.avg_time_to_booking_seconds != null
                  ? fmtSeconds(kpi.avg_time_to_booking_seconds)
                  : '—'}
              </div>
              <div className="kpi-sub">après publication</div>
            </>
          ) : (
            <div className="kpi-value muted">—</div>
          )}
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Remise moyenne</div>
          {kpi ? (
            <>
              <div className="kpi-value accent">
                {kpi.avg_discount_applied != null
                  ? `−${kpi.avg_discount_applied.toFixed(1)}%`
                  : '—'}
              </div>
              <div className="kpi-sub">appliquée par le pricing</div>
            </>
          ) : (
            <div className="kpi-value muted">—</div>
          )}
        </div>
      </div>

      {!kpi && !kpiLoading && (
        <div className="kpi-unavailable">
          Les KPI détaillés seront disponibles en Phase 4.
        </div>
      )}

      <hr className="divider" />

      <section aria-labelledby="active-offers-title">
        <div className="section-head active-offers-head">
          <div>
            <div id="active-offers-title" className="section-title">
              <span className="live-dot" /> Offres actives (live)
            </div>
            <div className="section-subtitle">
              Offres visibles maintenant sur le feed, par ordre d'expiration.
              {activeServerTime ? ` Actualisé ${formatDate(activeServerTime)}.` : ''}
            </div>
          </div>
          <div className="active-offers-tools">
            <span className="section-count">
              {kpi?.live_offers_count ?? activeOffers.length} active
              {(kpi?.live_offers_count ?? activeOffers.length) !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void refreshLiveData()}
              disabled={activeLoading || moreLoading}
            >
              ↻ Rafraîchir
            </button>
          </div>
        </div>

        {activeLoading ? (
          <div className="loading" aria-label="Chargement des offres actives">
            <div className="spinner" />
          </div>
        ) : activeError && activeOffers.length === 0 ? (
          <div className="empty card">
            <div className="empty-icon">!</div>
            <div className="empty-title">Impossible de charger les offres actives</div>
            <div className="empty-sub">{activeError}</div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void loadActiveOffers()}
            >
              Réessayer
            </button>
          </div>
        ) : activeOffers.length === 0 ? (
          <div className="empty card">
            <div className="empty-icon">✓</div>
            <div className="empty-title">Aucune offre active</div>
            <div className="empty-sub">Le feed public est actuellement vide.</div>
          </div>
        ) : (
          <>
            <div className="offer-grid active-offer-grid">
              {activeOffers.map((offer) => (
                <ActiveOfferCard
                  key={offer.id}
                  offer={offer}
                  busy={killingId === offer.id}
                  onCopy={() => void copyOfferId(offer.id)}
                  onKill={() =>
                    setKillTarget({ id: offer.id, title: offer.title })
                  }
                />
              ))}
            </div>

            {hasMore && nextCursor && (
              <div className="load-more-row">
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={moreLoading}
                  onClick={() => void loadActiveOffers(nextCursor, true)}
                >
                  {moreLoading ? <span className="spinner" /> : null}
                  Afficher plus
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <hr className="divider" />

      <details
        className="manual-kill-details"
        onToggle={(event) => {
          if (event.currentTarget.open) void loadKillSearchPool();
        }}
      >
        <summary>⚡ Retrait d'urgence par titre</summary>
        <div className="manual-kill-copy">
          Recours avancé pour retirer une offre active qui n'apparaît pas dans la
          page chargée. Recherchez par titre plutôt que par UUID.
        </div>
        <div className="card">
          <div className="field kill-search-field">
            <label htmlFor="kill-search">Titre de l'offre</label>
            <input
              id="kill-search"
              type="text"
              autoComplete="off"
              placeholder="Commencez à taper le titre de l'offre…"
              value={killSearchQuery}
              onChange={(event) => setKillSearchQuery(event.target.value)}
              onFocus={() => void loadKillSearchPool()}
              disabled={killingId != null}
            />
            {killSearchQuery.trim().length > 0 && (
              <div className="kill-search-results">
                {killSearchLoading && killSearchPool === null ? (
                  <div className="kill-search-empty">Recherche en cours…</div>
                ) : killSearchResults.length > 0 ? (
                  killSearchResults.map((offer) => (
                    <button
                      key={offer.id}
                      type="button"
                      className="kill-search-result"
                      disabled={killingId != null}
                      onClick={() => {
                        setKillTarget({ id: offer.id, title: offer.title });
                        setKillSearchQuery('');
                      }}
                    >
                      <span className="kill-search-result-title">
                        {offer.title}
                      </span>
                      <span className="kill-search-result-meta">
                        {formatPrice(offer.current_price)} ·{' '}
                        {formatTimeRemaining(offer.time_remaining_seconds)}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="kill-search-empty">
                    Aucune offre active ne correspond à « {killSearchQuery.trim()} ».
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </details>

      {killTarget && (
        <ConfirmModal
          title="Confirmer le retrait d'urgence"
          description={`${
            killTarget.title ? `« ${killTarget.title} »` : `L'offre ${killTarget.id}`
          } sera immédiatement retirée du feed et passera au statut "withdrawn". Cette action est irréversible. UUID : ${killTarget.id}`}
          confirmLabel="Retirer l'offre"
          confirmVariant="danger"
          onConfirm={() => void handleKill()}
          onCancel={() => setKillTarget(null)}
          loading={killingId === killTarget.id}
        />
      )}
    </>
  );
}

interface ActiveOfferCardProps {
  offer: ActiveOffer;
  busy: boolean;
  onCopy: () => void;
  onKill: () => void;
}

function ActiveOfferCard({
  offer,
  busy,
  onCopy,
  onKill,
}: ActiveOfferCardProps) {
  return (
    <article className="offer-card active-offer-card">
      <div className="active-offer-media">
        {offer.photo_url ? (
          <img src={offer.photo_url} alt={offer.title} className="offer-photo" />
        ) : (
          <div className="offer-photo-placeholder">🏠</div>
        )}
        <span className="active-offer-badge">
          <span className="live-dot" /> LIVE
        </span>
        <span className="active-offer-source">
          {offer.source_type === 'PARTICULIER' ? 'Cession' : 'Hôte'}
        </span>
      </div>

      <div className="offer-body">
        <div className="offer-title">{offer.title}</div>
        <div className="active-offer-prices">
          <span className="offer-price">{formatPrice(offer.current_price)}</span>
          {offer.reference_price > offer.current_price && (
            <span className="active-reference-price">
              {formatPrice(offer.reference_price)}
            </span>
          )}
        </div>
        <div className="offer-meta">
          <div className="offer-meta-row">
            <span className="icon">◎</span>
            {offer.location_label ?? 'Localisation non affichée'}
          </div>
          <div className="offer-meta-row active-offer-expiry">
            <span className="icon">◷</span>
            <span>
              Réservable encore {formatTimeRemaining(offer.time_remaining_seconds)} ·{' '}
              {formatDate(offer.expires_at)}
            </span>
          </div>
          <div className="offer-meta-row">
            <span className="icon">◷</span>
            <span>
              Séjour {formatDate(offer.starts_at)} → {formatDate(offer.checkout_at)}
            </span>
          </div>
        </div>
        {offer.description && <div className="offer-desc">{offer.description}</div>}
        <div className="offer-host">
          <span>Hôte&nbsp;</span>
          {offer.host_id ?? 'anonymisé'}
        </div>
        <div className="active-offer-id">
          <code>{offer.id}</code>
          <button type="button" className="copy-id-button" onClick={onCopy}>
            Copier
          </button>
        </div>
      </div>

      <div className="offer-actions">
        <button
          type="button"
          className="btn btn-danger btn-full"
          disabled={busy}
          onClick={onKill}
        >
          {busy ? <span className="spinner" /> : '⚡'}
          Retirer du feed
        </button>
      </div>
    </article>
  );
}
